import type { ReactNode } from 'react';

import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  SxProps,
  Theme,
} from '@mui/material';

/** A header cell's contents, or those contents plus styles layered over the default cell. */
export type GenericTableHeader = ReactNode | Readonly<{ content: ReactNode; sx?: SxProps<Theme> }>;

export type GenericTableProps = Readonly<{
  headers: readonly GenericTableHeader[];
  children: React.ReactNode;
  id?: string;
  ariaLabel?: string;
  stickyHeader?: boolean;
  containerSx?: SxProps<Theme>;
  tableSx?: SxProps<Theme>;
  bodySx?: SxProps<Theme>;
  /** Rendered inside the bordered container, below the table. */
  footer?: ReactNode;
}>;

const layer = (base: SxProps<Theme>, override?: SxProps<Theme>): SxProps<Theme> =>
  override == null ? base : [base, ...(Array.isArray(override) ? override : [override])];

const isStyledHeader = (
  header: GenericTableHeader,
): header is Readonly<{ content: ReactNode; sx?: SxProps<Theme> }> =>
  typeof header === 'object' && header !== null && 'content' in header;

export const GenericTable = ({
  headers,
  children,
  id,
  ariaLabel,
  stickyHeader = false,
  containerSx,
  tableSx,
  bodySx,
  footer,
}: GenericTableProps) => {
  return (
    <TableContainer id={id} sx={layer(tableContainerSx, containerSx)}>
      <Table size="small" aria-label={ariaLabel} stickyHeader={stickyHeader} sx={tableSx}>
        <TableHead>
          <TableRow>
            {headers.map((header, idx) => (
              <TableCell
                key={idx}
                sx={layer(tableHeaderLabelSx, isStyledHeader(header) ? header.sx : undefined)}
              >
                {isStyledHeader(header) ? header.content : header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody sx={layer(tableBodySx, bodySx)}>{children}</TableBody>
      </Table>
      {footer}
    </TableContainer>
  );
};

const tableContainerSx: SxProps<Theme> = ({ backrs }) => ({
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  borderRadius: backrs.radius['radius-md'],
});

const tableHeaderLabelSx: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-xs-semibold'],
  color: backrs.colors['Colors/Text/text-quaternary'],
  backgroundColor: backrs.colors['Colors/Background/bg-secondary'],
  paddingBlock: backrs.spacing['spacing-lg'],
  '&:first-of-type': {
    paddingLeft: backrs.spacing['spacing-3xl'],
  },
  '&:last-of-type': {
    paddingRight: backrs.spacing['spacing-3xl'],
  },
});

const tableBodySx: SxProps<Theme> = ({ backrs }) => ({
  '& tr:last-of-type td': {
    border: 0,
  },
  '& td:first-of-type': {
    paddingLeft: backrs.spacing['spacing-3xl'],
  },
  '& td:last-of-type': {
    paddingRight: backrs.spacing['spacing-3xl'],
  },
});
